import React, { useMemo, useState, useEffect } from "react";
import Button from "./../../../components/Button/Button"; // Assurez-vous du chemin
import { useNavigate, useLocation } from "react-router-dom";
import useScrollToTop from "./../../../hooks/useScollToTop";
import { getGroupsHome } from "./../../../services/groupServices";
import { getBeltsList } from "./../../../services/beltServices";
import {
  addNewAdherent,
  getAllAdherentsList,
} from "./../../../services/adherentServices";
import { assignAdherentToUser } from "./../../../services/userServices";
import { createNewDossierInscription } from "./../../../services/dossierServices";
import {
  sendNotification,
  sendNotificationToAdmin,
} from "./../../../services/notificationServices";
import { getSettings } from "./../../../services/settingsServices";
import { getGroupsList } from "./../../../services/groupServices";
import { getDojos } from "./../../../services/dojoServices";
import { useUser } from "../../Home/UserContext";
import { ToastContainer, toast } from "react-toastify";
import moment from "moment"; // Assurez-vous d'avoir installé moment
import {
  FaPlus,
  FaTimes,
  FaClock,
  FaUserFriends,
  FaEuroSign,
  FaMapPin,
} from "react-icons/fa";
import AdherentsMiniTable from "./AdherentsMiniTable";

// Fonction pour formater le prénom
const formatPrenom = (prenom) => {
  return prenom
    .split("-")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join("-");
};

const InscriptionPage = () => {
  useScrollToTop();
  const navigate = useNavigate();
  const { user } = useUser();
  const location = useLocation();
  const groupeIdFromState = location.state?.groupeId; // Accès à l'ID du groupe passé via le state de navigation
  const [belts, setBelts] = useState([]);
  const [saisonActive, setSaisonActive] = useState("");
  const [formData, setFormData] = useState({
    nom: "",
    prenom: "",
    genre: "",
    date_naissance: "",
    lieu_naissance: "",
    poids: "",
    licence_number: "",
    adresse: "",
    cp: "",
    commune: "",
    representant_legal: "",
    tel: "",
    tel_secondaire: "",
    email: "",
    email_secondaire: "",
    groupeId: "",
    statut_inscription: "",
    autorisation_photo: true,
    belt_id: "",
    date_passage: "",
    notes_adherent: "",
    saison_active: "",
    passsport_number: "",
  });

  const [userInfo, setUserInfo] = useState({
    nom: "",
    prenom: "",
    // Vous pouvez ajouter d'autres attributs au besoin
  });

  const [groupes, setGroupes] = useState([]); // Pour stocker les groupes récupérés de l'API
  const [selectedGroup, setSelectedGroup] = useState(null); // Le groupe sélectionné
  const [selectedGroups, setSelectedGroups] = useState([]);
  const [selectedGroupIdToAdd, setSelectedGroupIdToAdd] = useState("");
  const [dateError, setDateError] = useState("");
  const [groupError, setGroupError] = useState("");
  const [adherentsList, setAdherentsList] = useState([]);
  const [duplicateWarning, setDuplicateWarning] = useState("");
  const [dojos, setDojos] = useState([]);
  const [selectedDojo, setSelectedDojo] = useState(""); // Stocke l'ID du Dojo sélectionné
  const [isQuotaActive, setIsQuotaActive] = useState(false);

  // CommuneModification : Communes proposées selon le code postal
  const [communes, setCommunes] = useState([]);
  const [communeLoading, setCommuneLoading] = useState(false);

  useEffect(() => {
    const fetchBelts = async () => {
      const beltsData = await getBeltsList();
      setBelts(beltsData);
    };
    fetchBelts();
  }, []);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const settingsData = await getSettings();
        const isqa =
          settingsData?.isquotaactive ??
          settingsData?.[0]?.isquotaactive ??
          false;
        setIsQuotaActive(!!isqa);
        setSaisonActive(settingsData[0].saison_active); // Mise à jour pour utiliser les données de settings
        setFormData((prev) => ({
          ...prev,
          saison_active: settingsData[0].saison_active,
        }));
      } catch (error) {
        console.error("Erreur lors de la récupération des paramètres:", error);
      }
    };
    fetchSettings();
  }, []);

  useEffect(() => {
    // Tentez de récupérer les données utilisateur de localStorage
    const storedUserInfo = JSON.parse(localStorage.getItem("userInfo"));
    if (storedUserInfo) {
      setUserInfo({
        nom: storedUserInfo.nom || "",
        prenom: storedUserInfo.prenom || "",
        userId: storedUserInfo.userId || "",
        // Assurez-vous d'ajouter ici d'autres attributs si nécessaire
      });
    }
  }, []);

  useEffect(() => {
    const fetchGroups = async () => {
      try {
        const response = await getGroupsList();
        const activeGroups = response.filter((group) => group.is_active);

        if (selectedDojo) {
          setGroupes(
            activeGroups.filter(
              (group) => group.dojo_id === parseInt(selectedDojo)
            )
          );
        } else {
          setGroupes(activeGroups);
        }

        if (groupeIdFromState) {
          const selected = activeGroups.find(
            (groupe) => groupe.id.toString() === groupeIdFromState.toString()
          );
          setSelectedGroup(selected);
        }
      } catch (error) {
        console.error("Erreur lors du chargement des groupes:", error);
      }
    };

    fetchGroups();
  }, [groupeIdFromState, selectedDojo]); // 🔥 Ajout de selectedDojo comme dépendance

  useEffect(() => {
    const fetchDojos = async () => {
      try {
        const data = await getDojos();
        setDojos(data);
      } catch (error) {
        console.error("Erreur lors de la récupération des dojos", error);
        toast.error("Erreur lors de la récupération des dojos");
      }
    };

    fetchDojos();
  }, []);

  useEffect(() => {
    const fetchAdherentsList = async () => {
      try {
        const adherents = await getAllAdherentsList();
        setAdherentsList(adherents);
      } catch (error) {
        console.error(
          "Erreur lors de la récupération de la liste des adhérents:",
          error
        );
      }
    };

    fetchAdherentsList();
  }, []);

  // Utilisez `getGroupsList` pour récupérer les groupes avec le nombre d'adhérents
  useEffect(() => {
    const fetchGroups = async () => {
      try {
        const response = await getGroupsList(); // Utilisez la fonction importée
        const activeGroups = response.filter((group) => group.is_active); // Filtrer les groupes actifs
        setGroupes(activeGroups);

        if (groupeIdFromState) {
          // Logique pour gérer la sélection du groupe directement basé sur groupeIdFromState
          const selected = activeGroups.find(
            (groupe) => groupe.id.toString() === groupeIdFromState.toString()
          );
          setSelectedGroup(selected);
        }
      } catch (error) {
        console.error("Erreur lors du chargement des groupes:", error);
      }
    };

    fetchGroups();
  }, [groupeIdFromState]);

  // CommuneModification Récupération des communes selon le code postal
  useEffect(() => {
    const cp = formData.cp;
    if (!/^\d{5}$/.test(cp)) {
      setCommunes([]);
      setCommuneLoading(false);
      return;
    }

    let aborted = false;
    setCommuneLoading(true);

    fetch(
      `https://geo.api.gouv.fr/communes?codePostal=${cp}&fields=nom&format=json`
    )
      .then((r) => (r.ok ? r.json() : []))
      .then((data) => {
        if (aborted) return;
        const list = Array.isArray(data)
          ? data
              .map((c) => (c?.nom || "").toUpperCase())
              .filter(Boolean)
              .sort((a, b) => a.localeCompare(b, "fr"))
          : [];
        setCommunes(list);
        // Auto-sélection si une seule commune
        if (list.length === 1) {
          setFormData((prev) => ({ ...prev, commune: list[0] }));
        }
      })
      .catch(() => {
        if (!aborted) setCommunes([]);
      })
      .finally(() => {
        if (!aborted) setCommuneLoading(false);
      });

    return () => {
      aborted = true;
    };
  }, [formData.cp]);

  const isGroupFull = (g) =>
    Number(g?.nombreAdherents || 0) >= Number(g?.quota_groupe || 0);

  const checkForDuplicate = (nom, prenom) => {
    const duplicate = adherentsList.find(
      (adherent) => adherent.nom === nom && adherent.prenom === prenom
    );
    if (duplicate) {
      setDuplicateWarning(
        `Un adhérent avec le nom ${nom} et le prénom ${prenom} existe déjà.`
      );
    } else {
      setDuplicateWarning("");
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    let formattedValue = value;

    if (name === "tel") {
      // Retirer tous les espaces et autres caractères non-numériques
      formattedValue = value.replace(/\D/g, "");
    } else if (name === "nom") {
      // Autoriser les lettres, espaces et tirets
      formattedValue = value.toUpperCase().replace(/[^A-Z\s-]/g, "");
    } else if (name === "prenom") {
      // Autoriser les lettres (avec accents), espaces et tirets, supprimer les autres caractères
      formattedValue = formatPrenom(value.replace(/[^a-zA-ZÀ-ÿ\s-]/g, ""));
    } else if (name === "poids") {
      // Retirer tout ce qui n'est pas un chiffre ou un point (pour les décimales)
      formattedValue = value.replace(/[^0-9.]/g, "");
    } else if (name === "commune") {
      // Convertir en majuscules toute la chaîne
      formattedValue = value.toUpperCase();
    } else if (name === "cp") {
      // chiffres uniquement, 5 max
      formattedValue = value.replace(/\D/g, "").slice(0, 5);
      // CommuneModification reset des suggestions + commune si CP change
      setCommunes([]);
      setFormData((prev) => ({ ...prev, cp: formattedValue, commune: "" }));
      return; // on sort ici, pour ne pas refaire un setFormData en bas
    }

    setFormData((prev) => ({ ...prev, [name]: formattedValue }));

    if (name === "nom" || name === "prenom") {
      checkForDuplicate(
        name === "nom" ? formattedValue : formData.nom,
        name === "prenom" ? formattedValue : formData.prenom
      );
    }
  };

  const handleChangeGroup = (e) => {
    const groupeId = e.target.value;
    const groupe = groupes.find((g) => g.id.toString() === groupeId);

    if (!groupe) {
      setSelectedGroup(null);
      setGroupError("");
      return;
    }

    // Si quota actif et groupe plein → empêcher la sélection
    if (isQuotaActive && isGroupFull(groupe)) {
      setGroupError(
        `Le groupe ${groupe.nom} est complet (${groupe.quota_groupe} places). Veuillez en choisir un autre.`
      );
      setSelectedGroup(null);
      // Réinitialise visuellement le select
      e.target.value = "";
      return;
    }

    // Préparation des années naissance (comme avant)
    let updatedGroup = { ...groupe };
    if (typeof updatedGroup.annees_naissance === "string") {
      if (updatedGroup.annees_naissance.includes("à partir de 12 ans")) {
        updatedGroup.annees_naissance = ["à partir de 12 ans"];
      } else {
        updatedGroup.annees_naissance = updatedGroup.annees_naissance
          .split(/[-, ]+/)
          .map((item) => item.trim());
      }
    }

    setSelectedGroup(updatedGroup);

    // Message d’avertissement si plein mais quota non actif
    if (isGroupFull(updatedGroup) && !isQuotaActive) {
      setGroupError(
        `Le groupe ${updatedGroup.nom} a atteint son quota maximum de ${updatedGroup.quota_groupe} adhérents. (L'inscription reste possible car le blocage automatique est désactivé.)`
      );
    } else {
      setGroupError("");
    }
  };

  const validerEtFormatterDate = (dateStr) => {
    // Retirer tous les caractères non-numériques sauf '/'
    const cleaned = dateStr.replace(/[^0-9/]/g, "");
    // Essayer de construire une date avec les différents formats possibles
    const formats = ["DD/MM/YY", "DDMMYY", "DDMMYYYY", "DD/MM/YYYY"];
    for (let format of formats) {
      const momentDate = moment(cleaned, format, true);
      if (momentDate.isValid()) {
        return momentDate.format("YYYY-MM-DD"); // Format standard pour l'enregistrement
      }
    }
    return null; // Retourner null si aucun format n'est valide
  };

  const handleDateChange = (e) => {
    let { value } = e.target;
    let formattedValue = value.replace(/[^\d/]/g, "");

    if (formattedValue.length === 2 || formattedValue.length === 5) {
      if (formattedValue.slice(-1) !== "/") {
        formattedValue += "/";
      }
    }

    if (formattedValue.length > 10) {
      formattedValue = formattedValue.slice(0, 10);
    }

    setFormData((prev) => ({ ...prev, date_naissance: formattedValue }));

    if (formattedValue.length === 10) {
      const momentDate = moment(formattedValue, "DD/MM/YYYY", true);
      const isValidDate = momentDate.isValid();
      const isFutureDate = momentDate.isAfter(moment());

      if (!isValidDate || isFutureDate) {
        setDateError(
          "La date n'est pas valide ou est une date future. Veuillez entrer une date au format JJ/MM/AAAA."
        );
        setGroupError("");
        return;
      } else {
        setDateError("");
        setGroupError(""); // ⬅️ suppression de toute vérification liée au groupe ici
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Vérifier si les champs obligatoires sont remplis
    if (!formData.date_naissance || dateError) {
      toast.error(
        "Veuillez corriger les erreurs avant de soumettre le formulaire."
      );
      return;
    }

    if (!user || !user.userId) {
      toast.error("Vous devez être connecté pour effectuer cette action.");
      return; // Arrêter l'exécution si nous n'avons pas de données utilisateur valides
    }

    // Mise à jour de photo_url en fonction de l'état de autorisation_photo
    const photo_url = formData.autorisation_photo
      ? ""
      : "/img/interdiction.jpg";

    // Si aucun groupe n'a été ajouté manuellement mais qu'un groupe est sélectionné
    if (selectedGroups.length === 0 && selectedGroupIdToAdd) {
      const groupToAdd = groupes.find(
        (g) => g.id === parseInt(selectedGroupIdToAdd)
      );
      if (groupToAdd) {
        selectedGroups.push(groupToAdd); // Ajoute automatiquement le groupe sélectionné
      }
    }

    if (selectedGroups.length === 0) {
      toast.error("Veuillez sélectionner au moins un groupe.");
      return;
    }

    if (selectedGroups.length === 0 && selectedGroupIdToAdd) {
      toast.warn("Le groupe sélectionné a été automatiquement ajouté.");
    }

    // Inclusion des données de ceinture dans les données à envoyer
    const adherentData = {
      ...formData,
      date_naissance: validerEtFormatterDate(formData.date_naissance),
      groupes: selectedGroups.map((g) => g.id), // Assurez-vous que c'est un tableau d'IDs
      photo_url,
      belt_id: formData.belt_id,
      date_passage: formData.date_passage,
      saison_active: saisonActive,
    };

    try {
      const addResponse = await addNewAdherent(adherentData);

      // Vérifier que addResponse contient un `id` avant de continuer
      if (addResponse && addResponse.id) {
        const associationData = {
          userId: user.userId,
          adherentId: addResponse.id,
        };
        const assocResponse = await assignAdherentToUser(
          user.userId,
          addResponse.id,
          user.token
        );

        // Création du dossier d'inscription
        const dossierResponse = await createNewDossierInscription(
          addResponse.id
        );
        toast.success("Adhérent ajouté avec succès.");
        // Préparation de la notification
        // Send notification to user
        // Construction du nom des groupes sélectionnés
        const groupNames =
          selectedGroups.length > 0
            ? selectedGroups.map((g) => g.nom).join(", ")
            : "aucun groupe sélectionné";

        // ✅ Notification pour l'utilisateur
        const notificationUser = `Félicitations, vous venez d'inscrire ${formData.prenom} ${formData.nom} dans le(s) groupe(s) : ${groupNames}.`;
        sendNotification(notificationUser, user.userId, user.token);

        // Send notification to admin
        const notificationAdmin = `${userInfo.prenom} ${userInfo.nom} vient d'inscrire ${formData.prenom} ${formData.nom} dans le(s) groupe(s) : ${groupNames}.`;
        sendNotificationToAdmin(notificationAdmin, user.token);

        setFormData({
          // Clear the form
          nom: "",
          prenom: "",
          genre: "",
          date_naissance: "",
          lieu_naissance: "",
          poids: "",
          licence_number: "",
          adresse: "",
          cp: "",
          commune: "",
          representant_legal: "",
          tel: "",
          tel_secondaire: "",
          email: "",
          email_secondaire: "",
          groupeId: "",
          statut_inscription: "",
          belt_id: "",
          date_passage: "",
          notes_adherent: "",
          saison_active: saisonActive,
          passsport_number: "",
        });
        navigate("/dossier-d-inscription");
      } else {
        toast.error(
          "Erreur lors de l'ajout de l'adhérent. Veuillez réessayer."
        );
      }
    } catch (error) {
      toast.error("Erreur lors de l'ajout de l'adhérent. Veuillez réessayer.");
    }
  };

  // Calculer le tarif total
  const calculerTarifTotal = () => {
    if (selectedGroup) {
      return selectedGroup.tarif_licence + selectedGroup.tarif_cotisation;
    }
    return 0; // Retourner 0 si aucun groupe n'est sélectionné
  };

  // --- Helpers pour la section Dojo/Groupe ---

  // Liste des groupes affichés dans le <select> (filtrés par dojo + non déjà ajoutés)
  const groupesAffiches = useMemo(() => {
    const notAlready = groupes.filter(
      (g) => !selectedGroups.some((sel) => sel.id === g.id)
    );
    if (!selectedDojo) return notAlready;
    // Utile si "groupes" n'est pas encore filtré côté useEffect
    return notAlready.filter((g) => Number(g.dojo_id) === Number(selectedDojo));
  }, [groupes, selectedGroups, selectedDojo]);

  // Formatage € (licence / cotisation / total)
  const euro = (n) =>
    new Intl.NumberFormat("fr-FR", {
      style: "currency",
      currency: "EUR",
    }).format(Number(n) || 0);

  // Ajout du groupe sélectionné (avec contrôle de quota si activé)
  const addSelectedGroup = () => {
    if (!selectedGroupIdToAdd) return;

    const groupToAdd = groupes.find(
      (g) => g.id === Number(selectedGroupIdToAdd)
    );
    if (!groupToAdd) return;

    if (isQuotaActive && isGroupFull(groupToAdd)) {
      setGroupError(
        `Le groupe ${groupToAdd.nom} est complet (${groupToAdd.quota_groupe} places). Veuillez en choisir un autre.`
      );
      return;
    }

    setSelectedGroups([...selectedGroups, groupToAdd]);
    setSelectedGroupIdToAdd("");
    setGroupError("");
  };

  // Retirer un groupe des cartes sélectionnées
  const removeGroup = (id) => {
    setSelectedGroups(selectedGroups.filter((g) => g.id !== id));
  };

  // === Couleurs dynamiques depuis g.color_group (ex: "bg-blue-400") ===
  const COLOR_TOKEN_MAP = {
    "bg-orange-400": {
      light: "bg-orange-200",
      solid: "bg-orange-500",
      border: "border-orange-300",
    },
    "bg-indigo-400": {
      light: "bg-indigo-200",
      solid: "bg-indigo-500",
      border: "border-indigo-300",
    },
    "bg-purple-400": {
      light: "bg-purple-200",
      solid: "bg-purple-500",
      border: "border-purple-300",
    },
    "bg-green-400": {
      light: "bg-green-200",
      solid: "bg-green-500",
      border: "border-green-300",
    },
    "bg-yellow-400": {
      light: "bg-yellow-200",
      solid: "bg-yellow-500",
      border: "border-yellow-300",
    },
    "bg-blue-400": {
      light: "bg-blue-200",
      solid: "bg-blue-500",
      border: "border-blue-300",
    },
    "bg-red-400": {
      light: "bg-red-200",
      solid: "bg-red-500",
      border: "border-red-300",
    },
    "bg-pink-400": {
      light: "bg-pink-200",
      solid: "bg-pink-500",
      border: "border-pink-300",
    },
    "bg-gray-400": {
      light: "bg-gray-200",
      solid: "bg-gray-500",
      border: "border-gray-300",
    },
    "bg-cyan-400": {
      light: "bg-cyan-200",
      solid: "bg-cyan-500",
      border: "border-cyan-300",
    },
    "bg-teal-400": {
      light: "bg-teal-200",
      solid: "bg-teal-500",
      border: "border-teal-300",
    },
    "bg-emerald-400": {
      light: "bg-emerald-200",
      solid: "bg-emerald-500",
      border: "border-emerald-300",
    },
    "bg-lime-400": {
      light: "bg-lime-200",
      solid: "bg-lime-500",
      border: "border-lime-300",
    },
    "bg-amber-400": {
      light: "bg-amber-200",
      solid: "bg-amber-500",
      border: "border-amber-300",
    },
    "bg-rose-400": {
      light: "bg-rose-200",
      solid: "bg-rose-500",
      border: "border-rose-300",
    },
    "bg-violet-400": {
      light: "bg-violet-200",
      solid: "bg-violet-500",
      border: "border-violet-300",
    },
    "bg-sky-400": {
      light: "bg-sky-200",
      solid: "bg-sky-500",
      border: "border-sky-300",
    },
    "bg-fuchsia-400": {
      light: "bg-fuchsia-200",
      solid: "bg-fuchsia-500",
      border: "border-fuchsia-300",
    },
    "bg-zinc-400": {
      light: "bg-zinc-200",
      solid: "bg-zinc-500",
      border: "border-zinc-300",
    },

    // cas particuliers
    "bg-gray-500": {
      light: "bg-gray-300",
      solid: "bg-gray-600",
      border: "border-gray-400",
    },
  };

  const dojoById = useMemo(() => {
    const map = {};
    for (const d of dojos) map[d.id] = d;
    return map;
  }, [dojos]);

  const getLightColorClass = (c) => COLOR_TOKEN_MAP[c]?.light || "bg-slate-200";
  const getSolidColorClass = (c) => COLOR_TOKEN_MAP[c]?.solid || "bg-slate-400";
  const getBorderColorClass = (c) =>
    COLOR_TOKEN_MAP[c]?.border || "border-slate-300";

  return (
    <div className="container mx-auto pt-[150px] px-4 mb-10 font-montserrat">
      {userInfo.nom && userInfo.prenom && (
        <h2 className="text-normal italic font-normal mb-2">
          Bienvenue {userInfo.prenom} {userInfo.nom} sur la page d'inscription
          des adhérents
        </h2>
      )}
      <AdherentsMiniTable className="mt-2" />

      <h2 className="text-2xl uppercase font-bold mb-4">
        Formulaire d'Inscription pour la saison {saisonActive}
      </h2>
      {/* Image en en-tête */}
      <div className="text-center mb-4">
        <img src="/img/inscription.png" alt="Inscription" className="mx-auto" />
      </div>
      <div className="text-red-500 text-sm mb-4">* : champs obligatoires</div>

      <form onSubmit={handleSubmit} className="space-y-4">
        {/* ÉTAPE 1 — Saison (verrouillée) & Type d’inscription */}
        <section className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-4 md:p-5">
          <h3 className="text-base font-semibold text-slate-900 dark:text-slate-100 mb-3">
            Étape 1 — Saison & type d’inscription
          </h3>

          <div className="flex flex-wrap items-center gap-4">
            {/* Saison (verrouillée) */}
            <div className="flex items-center gap-3">
              <span className="text-sm text-slate-800 dark:text-slate-200">
                Saison<span className="text-red-500 ml-1">*</span>
              </span>
              <div
                className="inline-flex items-center rounded-lg px-3 py-1.5 min-h-[36px]
                      bg-slate-100 text-slate-900
                      dark:bg-slate-800 dark:text-slate-100
                      border border-slate-300 dark:border-slate-600"
              >
                {saisonActive ? (
                  <span className="font-medium text-sm">{saisonActive}</span>
                ) : (
                  <span className="inline-block h-4 w-24 rounded animate-pulse bg-slate-200 dark:bg-slate-700" />
                )}
              </div>
              {/* Valeur envoyée mais non modifiable */}
              <input
                type="hidden"
                name="saison_active"
                value={formData.saison_active}
              />
            </div>

            {/* séparateur visuel (largeurs md+) */}
            <div className="hidden md:block h-6 w-px bg-slate-200 dark:bg-slate-700" />

            {/* Type d’inscription (toggle compact) */}
            <div className="flex items-center gap-3">
              <span className="text-sm text-slate-800 dark:text-slate-200">
                Type d’inscription<span className="text-red-500 ml-1">*</span>
              </span>

              <div
                role="radiogroup"
                aria-label="Type d’inscription"
                className="inline-flex rounded-lg overflow-hidden border border-slate-300 dark:border-slate-600 text-sm"
              >
                {/* Cours d’essai */}
                <label className="cursor-pointer">
                  <input
                    type="radio"
                    name="statut_inscription"
                    value="Cours d'essai"
                    checked={formData.statut_inscription === "Cours d'essai"}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        statut_inscription: e.target.value,
                      })
                    }
                    className="sr-only"
                    required
                  />
                  <span
                    className={`block px-3 py-1.5
            ${
              formData.statut_inscription === "Cours d'essai"
                ? "bg-primary text-white"
                : "bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200 hover:bg-slate-200 dark:hover:bg-slate-700"
            }`}
                  >
                    Cours d’essai
                  </span>
                </label>

                {/* Inscription */}
                <label className="cursor-pointer border-l border-slate-300 dark:border-slate-600">
                  <input
                    type="radio"
                    name="statut_inscription"
                    value="Inscrit"
                    checked={formData.statut_inscription === "Inscrit"}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        statut_inscription: e.target.value,
                      })
                    }
                    className="sr-only"
                    required
                  />
                  <span
                    className={`block px-3 py-1.5
            ${
              formData.statut_inscription === "Inscrit"
                ? "bg-primary text-white"
                : "bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200 hover:bg-slate-200 dark:hover:bg-slate-700"
            }`}
                  >
                    Inscription
                  </span>
                </label>
              </div>
            </div>
          </div>

          {!formData.statut_inscription && (
            <p className="mt-2 text-xs text-amber-700 dark:text-amber-300">
              Choisissez un type pour continuer.
            </p>
          )}
        </section>

        {/* ÉTAPE 2 — Choix du dojo & du/des groupe(s) */}
        <section className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-4 md:p-5">
          <h3 className="text-base font-semibold text-slate-900 dark:text-slate-100">
            Étape 2 — Choix du Lieu & du/des groupe(s)
          </h3>

          <p className="text-xs mt-1 mb-3 text-slate-600 dark:text-slate-300">
            Vous pouvez sélectionner{" "}
            <span className="font-medium">un ou plusieurs</span> groupes.
          </p>

          {!formData.statut_inscription ? (
            <div className="rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/40 p-3 text-xs text-slate-700 dark:text-slate-200">
              Choisissez d’abord la{" "}
              <span className="font-semibold">saison</span> et le{" "}
              <span className="font-semibold">type d’inscription</span> (Étape
              1).
            </div>
          ) : (
            <>
              {/* Sélecteur de groupe avec card pour les informations */}
              {/* Ligne de sélection Dojo + Groupe + Ajouter */}
              <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-end mb-4">
                {/* Dojo */}
                <div className="md:col-span-4">
                  <label className="label mb-2 block text-slate-800 dark:text-slate-200">
                    Lieu<span className="text-red-500 ml-1">*</span>
                  </label>
                  <select
                    name="dojoId"
                    value={selectedDojo}
                    onChange={(e) => setSelectedDojo(e.target.value)}
                    required
                    className="input w-full px-3 py-2 rounded border
             bg-white text-slate-900
             dark:bg-slate-800 dark:text-slate-100
             border-slate-300 dark:border-slate-600
             focus:outline-none focus:ring-2 focus:ring-violet-400
             placeholder-slate-400 dark:placeholder-slate-400
             caret-violet-500"
                  >
                    <option value="">Sélectionnez un Lieu</option>
                    {dojos.map((dojo) => (
                      <option key={dojo.id} value={dojo.id}>
                        {dojo.dojo_nom}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Groupe */}
                <div className="md:col-span-6">
                  <label className="label mb-2 block text-slate-800 dark:text-slate-200">
                    Groupe<span className="text-red-500 ml-1">*</span>
                    <span className="text-gray-700 dark:text-gray-300 italic font-light ml-1 text-sm">
                      (un ou plusieurs groupes)
                    </span>
                  </label>
                  <select
                    value={selectedGroupIdToAdd}
                    onChange={(e) => setSelectedGroupIdToAdd(e.target.value)}
                    className="input w-full px-3 py-2 rounded border
             bg-white text-slate-900
             dark:bg-slate-800 dark:text-slate-100
             border-slate-300 dark:border-slate-600
             focus:outline-none focus:ring-2 focus:ring-violet-400
             placeholder-slate-400 dark:placeholder-slate-400
             caret-violet-500"
                  >
                    <option value="">
                      {selectedDojo
                        ? "Sélectionnez un groupe"
                        : "Choisissez d'abord un dojo"}
                    </option>
                    {groupesAffiches.map((g) => (
                      <option key={g.id} value={g.id}>
                        {g.nom}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Bouton Ajouter */}
                <div className="md:col-span-2">
                  <button
                    type="button"
                    onClick={addSelectedGroup}
                    disabled={!selectedGroupIdToAdd}
                    className="w-full inline-flex items-center justify-center gap-2 rounded-xl px-4 py-2 font-medium text-white
                       bg-primary
                       hover:brightness-110 active:scale-[0.99]
                       disabled:opacity-50 disabled:cursor-not-allowed shadow-md"
                    title="Ajouter le groupe sélectionné"
                  >
                    <FaPlus /> Je m'inscris
                  </button>
                </div>
              </div>

              {/* Alerte si aucun groupe */}
              {selectedGroups.length === 0 && (
                <div className="rounded-xl border border-amber-300 bg-amber-50 text-amber-900 dark:bg-amber-900/20 dark:text-amber-100 dark:border-amber-700 px-4 py-3">
                  ⚠️ Aucun groupe ajouté. Sélectionnez un groupe puis cliquez
                  sur
                  <span className="font-semibold"> “Je m'inscris”</span>.
                </div>
              )}

              {/* Cartes des groupes sélectionnés */}
              {selectedGroups.length > 0 && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                  {selectedGroups.map((g) => {
                    const horaires = [
                      g.horaire1,
                      g.horaire2,
                      g.horaire3,
                      g.horaire4,
                    ].filter(Boolean);
                    const enseignants = [
                      g.enseignant1,
                      g.enseignant2,
                      g.enseignant3,
                      g.enseignant4,
                      g.enseignant5,
                      g.enseignant6,
                    ].filter(Boolean);
                    const cot = Number(g.tarif_cotisation) || 0;
                    const lic = Number(g.tarif_licence) || 0;
                    const total = cot + lic;
                    const inscrits = Number(g.nombreAdherents) || 0;
                    const quota = Number(g.quota_groupe) || 0;
                    const pct =
                      quota > 0
                        ? Math.min(100, Math.round((inscrits / quota) * 100))
                        : 0;
                    const borderClass = getBorderColorClass(g.color_groupe);
                    const barClass = getSolidColorClass(g.color_groupe);
                    const trackClass = getLightColorClass(g.color_groupe);

                    return (
                      <article
                        key={g.id}
                        className={
                          `relative rounded-2xl overflow-hidden bg-white dark:bg-slate-900 shadow-sm hover:shadow-md transition-shadow border ` +
                          `${borderClass} dark:border-opacity-60`
                        }
                      >
                        {/* Barre couleur gauche (10px) basée sur color_group */}
                        <div className="absolute inset-y-0 left-0 w-[10px]">
                          <div className={`${barClass} w-full h-full`} />
                        </div>

                        {/* Bouton retirer */}
                        <button
                          type="button"
                          onClick={() => removeGroup(g.id)}
                          className="absolute top-2 right-2 inline-flex items-center justify-center rounded-full w-9 h-9
                             bg-white/80 dark:bg-slate-800/80 backdrop-blur border border-slate-200 dark:border-slate-700
                             text-red-600 hover:bg-white dark:hover:bg-slate-800 transition"
                          title="Retirer ce groupe"
                        >
                          <FaTimes />
                        </button>

                        <div className="pl-[18px] pr-4 py-4">
                          {" "}
                          {/* padding pour dégager la barre gauche */}
                          {/* Titre */}
                          <h3 className="text-lg font-semibold mb-3 bg-gradient-to-r from-slate-200 to-slate-100 dark:from-slate-800 dark:to-slate-700 text-slate-900 dark:text-slate-100 px-3 py-2 rounded-lg">
                            {g.nom}
                          </h3>
                          {/* Badge Dojo */}
                          {(() => {
                            const dojo = dojoById[g.dojo_id];
                            const dojoNom = dojo?.dojo_nom || "Dojo inconnu";

                            return (
                              <div className="mb-3">
                                <span
                                  className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs font-medium
                        ${trackClass} ${borderClass} dark:opacity-90`}
                                >
                                  <FaMapPin className="text-[10px] opacity-80" />
                                  {dojoNom}
                                </span>
                              </div>
                            );
                          })()}
                          {/* Horaires */}
                          {horaires.length > 0 && (
                            <div className="mb-3">
                              <div className="text-sm font-medium text-slate-600 dark:text-slate-300 mb-1 inline-flex items-center gap-2">
                                <FaClock /> Horaires
                              </div>
                              <div className="flex flex-wrap gap-2">
                                {horaires.map((h, idx) => (
                                  <span
                                    key={idx}
                                    className="px-2.5 py-1 rounded-full text-xs font-medium bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200"
                                  >
                                    {h}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}
                          {/* Enseignants */}
                          {enseignants.length > 0 && (
                            <div className="mb-3">
                              <div className="text-sm font-medium text-slate-600 dark:text-slate-300 mb-1 inline-flex items-center gap-2">
                                <FaUserFriends /> Enseignant(s)
                              </div>
                              <div className="flex flex-wrap gap-2">
                                {enseignants.map((e, idx) => (
                                  <span
                                    key={idx}
                                    className="px-2.5 py-1 rounded-full text-xs font-medium bg-violet-50 dark:bg-violet-900/30 text-violet-700 dark:text-violet-200 border border-violet-200 dark:border-violet-700"
                                  >
                                    {e}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}
                          {/* Années de naissance */}
                          {g.annees_naissance && (
                            <div className="mb-3">
                              <div className="text-sm font-medium text-slate-600 dark:text-slate-300 mb-1">
                                Années de naissance
                              </div>
                              <div className="flex flex-wrap gap-2">
                                {(Array.isArray(g.annees_naissance)
                                  ? g.annees_naissance
                                  : String(g.annees_naissance).split(",")
                                )
                                  .map((s) => s.trim())
                                  .filter(Boolean)
                                  .map((an, idx) => (
                                    <span
                                      key={idx}
                                      className="px-2.5 py-1 rounded-full text-xs font-medium bg-sky-50 dark:bg-sky-900/30 text-sky-700 dark:text-sky-200 border border-sky-200 dark:border-sky-700"
                                    >
                                      {an}
                                    </span>
                                  ))}
                              </div>
                            </div>
                          )}
                          {/* Tarifs */}
                          <div className="mb-3">
                            <div className="text-sm font-medium text-slate-600 dark:text-slate-300 mb-1 inline-flex items-center gap-2">
                              <FaEuroSign /> Tarifs
                            </div>
                            <div className="grid grid-cols-3 gap-2 text-sm">
                              <div className="rounded-lg bg-slate-50 dark:bg-slate-800 px-3 py-2">
                                <div className="text-slate-500 dark:text-slate-400">
                                  Licence
                                </div>
                                <div className="font-semibold text-slate-900 dark:text-slate-100">
                                  {euro(lic)}
                                </div>
                              </div>
                              <div className="rounded-lg bg-slate-50 dark:bg-slate-800 px-3 py-2">
                                <div className="text-slate-500 dark:text-slate-400">
                                  Cotisation
                                </div>
                                <div className="font-semibold text-slate-900 dark:text-slate-100">
                                  {euro(cot)}
                                </div>
                              </div>
                              <div className="rounded-lg bg-emerald-50 dark:bg-emerald-900/30 px-3 py-2 border border-emerald-200 dark:border-emerald-700">
                                <div className="text-emerald-700 dark:text-emerald-200">
                                  Total
                                </div>
                                <div className="font-semibold text-emerald-800 dark:text-emerald-100">
                                  {euro(total)}
                                </div>
                              </div>
                            </div>
                          </div>
                          {/* Info groupe (HTML) */}
                          {g.info_groupe && (
                            <div className="mb-4">
                              <div className="text-sm font-medium text-slate-600 dark:text-slate-300 mb-1 underline">
                                Informations Groupe
                              </div>
                              <div
                                className="prose prose-sm max-w-none dark:prose-invert text-slate-800 dark:text-slate-200 text-sm"
                                // Assurez-vous de purifier côté back si nécessaire
                                dangerouslySetInnerHTML={{
                                  __html: g.info_groupe,
                                }}
                              />
                            </div>
                          )}
                          {/* Quota & jauge */}
                          <div className="mt-2">
                            <div className="flex items-center justify-between text-sm mb-1">
                              <span className="text-slate-700 dark:text-slate-300">
                                Quota:{" "}
                                <span className="font-semibold text-slate-900 dark:text-slate-100">
                                  {quota || "—"}
                                </span>{" "}
                                adhérents
                              </span>
                              <span className="text-slate-700 dark:text-slate-300">
                                Inscrits:{" "}
                                <span className="font-semibold text-slate-900 dark:text-slate-100">
                                  {inscrits}
                                </span>
                              </span>
                            </div>
                            <div
                              className={`h-2 rounded-full overflow-hidden ${trackClass} dark:opacity-80`}
                            >
                              <div
                                className={`h-2 rounded-full ${
                                  pct >= 100 ? "bg-rose-500" : barClass
                                }`}
                                style={{ width: `${pct}%` }}
                                title={`${pct}%`}
                              />
                            </div>
                            {pct >= 100 && (
                              <p className="mt-1 text-xs text-rose-600 dark:text-rose-300">
                                Groupe complet — vous pouvez en choisir un
                                autre.
                              </p>
                            )}
                          </div>
                        </div>
                      </article>
                    );
                  })}
                </div>
              )}

              {/* {groupError && (
                <div className="text-red-500 text-sm mb-4">{groupError}</div>
              )} */}

              {/* Message d’alerte global si besoin */}
              {groupError && (
                <div className="mt-3 rounded-lg border border-rose-300 dark:border-rose-700 bg-rose-50 dark:bg-rose-900/20 px-3 py-2 text-xs text-rose-700 dark:text-rose-200">
                  {groupError}
                </div>
              )}
            </>
          )}
        </section>

        {/* ÉTAPE 3 — Coordonnées de l’adhérent */}
        <section className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-4 md:p-5">
          <h3 className="text-base font-semibold text-slate-900 dark:text-slate-100">
            Étape 3 — Coordonnées de l’adhérent
          </h3>
          <p className="text-xs mt-1 mb-3 text-slate-600 dark:text-slate-300">
            Les champs marqués d’un <span className="text-red-500">*</span> sont
            obligatoires.
          </p>

          <div className="space-y-3">
            {/* Nom */}
            <div className="grid grid-cols-1 md:grid-cols-[180px_1fr] items-center gap-3">
              <label
                htmlFor="inp-nom"
                className="text-sm text-slate-800 dark:text-slate-200 md:text-right"
              >
                Nom<span className="text-red-500 ml-1">*</span>
              </label>
              <input
                id="inp-nom"
                name="nom"
                type="text"
                value={formData.nom}
                onChange={handleChange}
                required
                className="input w-full px-3 py-2 rounded border
                   bg-white text-slate-900 dark:bg-slate-800 dark:text-slate-100
                   border-slate-300 dark:border-slate-600
                   focus:outline-none focus:ring-2 focus:ring-violet-400
                   placeholder-slate-400 dark:placeholder-slate-400 caret-violet-500"
              />
            </div>

            {/* Prénom */}
            <div className="grid grid-cols-1 md:grid-cols-[180px_1fr] items-center gap-3">
              <label
                htmlFor="inp-prenom"
                className="text-sm text-slate-800 dark:text-slate-200 md:text-right"
              >
                Prénom<span className="text-red-500 ml-1">*</span>
              </label>
              <input
                id="inp-prenom"
                name="prenom"
                type="text"
                value={formData.prenom}
                onChange={handleChange}
                required
                className="input w-full px-3 py-2 rounded border
                   bg-white text-slate-900 dark:bg-slate-800 dark:text-slate-100
                   border-slate-300 dark:border-slate-600
                   focus:outline-none focus:ring-2 focus:ring-violet-400
                   placeholder-slate-400 dark:placeholder-slate-400 caret-violet-500"
              />
            </div>

            {/* Avertissement doublon (si présent) */}
            {duplicateWarning && (
              <div className="md:ml-[180px] text-xs text-rose-700 dark:text-rose-300">
                {duplicateWarning}
              </div>
            )}

            {/* Genre */}
            <div className="grid grid-cols-1 md:grid-cols-[180px_1fr] items-center gap-3">
              <label
                htmlFor="sel-genre"
                className="text-sm text-slate-800 dark:text-slate-200 md:text-right"
              >
                Genre<span className="text-red-500 ml-1">*</span>
              </label>
              <select
                id="sel-genre"
                name="genre"
                value={formData.genre}
                onChange={handleChange}
                required
                className="input w-full px-3 py-2 rounded border
                   bg-white text-slate-900 dark:bg-slate-800 dark:text-slate-100
                   border-slate-300 dark:border-slate-600
                   focus:outline-none focus:ring-2 focus:ring-violet-400
                   placeholder-slate-400 dark:placeholder-slate-400 caret-violet-500"
              >
                <option value="">Sélectionnez le genre</option>
                <option value="Masculin">Masculin</option>
                <option value="Féminin">Féminin</option>
              </select>
            </div>

            {/* Date de naissance */}
            <div className="grid grid-cols-1 md:grid-cols-[180px_1fr] items-center gap-3">
              <label
                htmlFor="inp-dn"
                className="text-sm text-slate-800 dark:text-slate-200 md:text-right"
              >
                Date de naissance<span className="text-red-500 ml-1">*</span>
              </label>
              <div>
                <input
                  id="inp-dn"
                  name="date_naissance"
                  type="text"
                  placeholder="JJ/MM/AAAA"
                  value={formData.date_naissance}
                  onChange={handleDateChange}
                  className="input w-full px-3 py-2 rounded border
                     bg-white text-slate-900 dark:bg-slate-800 dark:text-slate-100
                     border-slate-300 dark:border-slate-600
                     focus:outline-none focus:ring-2 focus:ring-violet-400
                     placeholder-slate-400 dark:placeholder-slate-400 caret-violet-500"
                />
                {dateError && (
                  <p className="mt-1 text-xs text-rose-700 dark:text-rose-300">
                    {dateError}
                  </p>
                )}
              </div>
            </div>

            {/* Lieu de naissance */}
            <div className="grid grid-cols-1 md:grid-cols-[180px_1fr] items-center gap-3">
              <label
                htmlFor="inp-lieu"
                className="text-sm text-slate-800 dark:text-slate-200 md:text-right"
              >
                Lieu de naissance<span className="text-red-500 ml-1">*</span>
              </label>
              <input
                id="inp-lieu"
                name="lieu_naissance"
                type="text"
                value={formData.lieu_naissance}
                onChange={handleChange}
                required
                className="input w-full px-3 py-2 rounded border
                   bg-white text-slate-900 dark:bg-slate-800 dark:text-slate-100
                   border-slate-300 dark:border-slate-600
                   focus:outline-none focus:ring-2 focus:ring-violet-400
                   placeholder-slate-400 dark:placeholder-slate-400 caret-violet-500"
              />
            </div>

            {/* Adresse */}
            <div className="grid grid-cols-1 md:grid-cols-[180px_1fr] items-center gap-3">
              <label
                htmlFor="inp-adresse"
                className="text-sm text-slate-800 dark:text-slate-200 md:text-right"
              >
                Adresse<span className="text-red-500 ml-1">*</span>
              </label>
              <input
                id="inp-adresse"
                name="adresse"
                type="text"
                value={formData.adresse}
                onChange={handleChange}
                required
                className="input w-full px-3 py-2 rounded border
                   bg-white text-slate-900 dark:bg-slate-800 dark:text-slate-100
                   border-slate-300 dark:border-slate-600
                   focus:outline-none focus:ring-2 focus:ring-violet-400
                   placeholder-slate-400 dark:placeholder-slate-400 caret-violet-500"
              />
            </div>

            {/* Code postal */}
            <div className="grid grid-cols-1 md:grid-cols-[180px_1fr] items-center gap-3">
              <label
                htmlFor="inp-cp"
                className="text-sm text-slate-800 dark:text-slate-200 md:text-right"
              >
                Code postal<span className="text-red-500 ml-1">*</span>
              </label>
              <input
                id="inp-cp"
                name="cp"
                type="text"
                pattern="\d{5}"
                inputMode="numeric"
                value={formData.cp}
                onChange={handleChange}
                required
                className="input w-full max-w-[220px] px-3 py-2 rounded border
                   bg-white text-slate-900 dark:bg-slate-800 dark:text-slate-100
                   border-slate-300 dark:border-slate-600
                   focus:outline-none focus:ring-2 focus:ring-violet-400
                   placeholder-slate-400 dark:placeholder-slate-400 caret-violet-500"
              />
            </div>

            {/* CommuneModification */}
            <div className="grid grid-cols-1 md:grid-cols-[180px_1fr] items-center gap-3">
              <label
                htmlFor="inp-commune"
                className="text-sm text-slate-800 dark:text-slate-200 md:text-right"
              >
                Commune<span className="text-red-500 ml-1">*</span>
              </label>

              <div className="flex flex-col gap-1">
                {communes.length > 0 ? (
                  <select
                    id="inp-commune"
                    name="commune"
                    value={formData.commune}
                    onChange={handleChange}
                    required
                    className="input w-full px-3 py-2 rounded border
          bg-white text-slate-900 dark:bg-slate-800 dark:text-slate-100
          border-slate-300 dark:border-slate-600
          focus:outline-none focus:ring-2 focus:ring-violet-400
          placeholder-slate-400 dark:placeholder-slate-400 caret-violet-500"
                  >
                    <option value="">
                      {communeLoading
                        ? "Chargement..."
                        : "Sélectionnez votre commune"}
                    </option>
                    {communes.map((c) => (
                      <option key={c} value={c}>
                        {c}
                      </option>
                    ))}
                  </select>
                ) : (
                  <input
                    id="inp-commune"
                    name="commune"
                    type="text"
                    value={formData.commune}
                    onChange={handleChange}
                    required
                    placeholder={communeLoading ? "Chargement..." : "Commune"}
                    className="input w-full px-3 py-2 rounded border
          bg-white text-slate-900 dark:bg-slate-800 dark:text-slate-100
          border-slate-300 dark:border-slate-600
          focus:outline-none focus:ring-2 focus:ring-violet-400
          placeholder-slate-400 dark:placeholder-slate-400 caret-violet-500"
                  />
                )}

                {/* aide sous le champ */}
                {formData.cp && formData.cp.length < 5 && (
                  <p className="text-xs text-slate-500 dark:text-slate-400">
                    Saisissez un code postal à 5 chiffres pour obtenir la liste
                    des communes.
                  </p>
                )}
              </div>
            </div>

            {/* Téléphone */}
            <div className="grid grid-cols-1 md:grid-cols-[180px_1fr] items-center gap-3">
              <label
                htmlFor="inp-tel"
                className="text-sm text-slate-800 dark:text-slate-200 md:text-right"
              >
                Téléphone<span className="text-red-500 ml-1">*</span>
              </label>
              <input
                id="inp-tel"
                name="tel"
                type="tel"
                pattern="^\+?\d{0,15}"
                value={formData.tel}
                onChange={handleChange}
                required
                className="input w-full px-3 py-2 rounded border
                   bg-white text-slate-900 dark:bg-slate-800 dark:text-slate-100
                   border-slate-300 dark:border-slate-600
                   focus:outline-none focus:ring-2 focus:ring-violet-400
                   placeholder-slate-400 dark:placeholder-slate-400 caret-violet-500"
              />
            </div>

            {/* Téléphone secondaire */}
            <div className="grid grid-cols-1 md:grid-cols-[180px_1fr] items-center gap-3">
              <label
                htmlFor="inp-tel2"
                className="text-sm text-slate-800 dark:text-slate-200 md:text-right"
              >
                Téléphone secondaire
              </label>
              <input
                id="inp-tel2"
                name="tel_secondaire"
                type="tel"
                pattern="^\+?\d{0,15}"
                value={formData.tel_secondaire}
                onChange={handleChange}
                className="input w-full px-3 py-2 rounded border
                   bg-white text-slate-900 dark:bg-slate-800 dark:text-slate-100
                   border-slate-300 dark:border-slate-600
                   focus:outline-none focus:ring-2 focus:ring-violet-400
                   placeholder-slate-400 dark:placeholder-slate-400 caret-violet-500"
              />
            </div>

            {/* Email */}
            <div className="grid grid-cols-1 md:grid-cols-[180px_1fr] items-center gap-3">
              <label
                htmlFor="inp-email"
                className="text-sm text-slate-800 dark:text-slate-200 md:text-right"
              >
                Email<span className="text-red-500 ml-1">*</span>
              </label>
              <input
                id="inp-email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                required
                className="input w-full px-3 py-2 rounded border
                   bg-white text-slate-900 dark:bg-slate-800 dark:text-slate-100
                   border-slate-300 dark:border-slate-600
                   focus:outline-none focus:ring-2 focus:ring-violet-400
                   placeholder-slate-400 dark:placeholder-slate-400 caret-violet-500"
              />
            </div>

            {/* Email secondaire */}
            <div className="grid grid-cols-1 md:grid-cols-[180px_1fr] items-center gap-3">
              <label
                htmlFor="inp-email2"
                className="text-sm text-slate-800 dark:text-slate-200 md:text-right"
              >
                Email secondaire
              </label>
              <input
                id="inp-email2"
                name="email_secondaire"
                type="email"
                value={formData.email_secondaire}
                onChange={handleChange}
                className="input w-full px-3 py-2 rounded border
                   bg-white text-slate-900 dark:bg-slate-800 dark:text-slate-100
                   border-slate-300 dark:border-slate-600
                   focus:outline-none focus:ring-2 focus:ring-violet-400
                   placeholder-slate-400 dark:placeholder-slate-400 caret-violet-500"
              />
            </div>

            {/* Représentant légal */}
            <div className="grid grid-cols-1 md:grid-cols-[180px_1fr] items-center gap-3">
              <label
                htmlFor="inp-rep"
                className="text-sm text-slate-800 dark:text-slate-200 md:text-right"
              >
                Représentant légal
              </label>
              <input
                id="inp-rep"
                name="representant_legal"
                type="text"
                value={formData.representant_legal}
                onChange={handleChange}
                className="input w-full px-3 py-2 rounded border
                   bg-white text-slate-900 dark:bg-slate-800 dark:text-slate-100
                   border-slate-300 dark:border-slate-600
                   focus:outline-none focus:ring-2 focus:ring-violet-400
                   placeholder-slate-400 dark:placeholder-slate-400 caret-violet-500"
              />
            </div>
          </div>
        </section>

        {/* ÉTAPE 4 — Informations complémentaires */}
        <section className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-4 md:p-5">
          <h3 className="text-base font-semibold text-slate-900 dark:text-slate-100">
            Étape 4 — Informations complémentaires
          </h3>
          <div className="text-xs mt-1 mb-3 text-slate-600 dark:text-slate-300">
            Renseignez les informations additionnelles de l’adhérent.
          </div>

          <div className="space-y-3">
            {/* Poids */}
            <div className="grid grid-cols-1 md:grid-cols-[180px_1fr] items-center gap-3">
              <label
                htmlFor="inp-poids"
                className="text-sm text-slate-800 dark:text-slate-200 md:text-right"
              >
                Poids (kg)
              </label>
              <input
                id="inp-poids"
                name="poids"
                type="number"
                min="1"
                step="0.1"
                placeholder="ex. 70.5"
                value={formData.poids}
                onChange={handleChange}
                className="input w-full max-w-[220px] px-3 py-2 rounded border
                   bg-white text-slate-900 dark:bg-slate-800 dark:text-slate-100
                   border-slate-300 dark:border-slate-600
                   focus:outline-none focus:ring-2 focus:ring-violet-400
                   placeholder-slate-400 dark:placeholder-slate-400 caret-violet-500"
              />
            </div>

            {/* Numéro de licence */}
            <div className="grid grid-cols-1 md:grid-cols-[180px_1fr] items-center gap-3">
              <label
                htmlFor="inp-licence"
                className="text-sm text-slate-800 dark:text-slate-200 md:text-right"
              >
                N° de licence
              </label>
              <div>
                <input
                  id="inp-licence"
                  name="licence_number"
                  type="text"
                  placeholder="Laissez vide pour débutants"
                  value={formData.licence_number}
                  onChange={handleChange}
                  className="input w-full px-3 py-2 rounded border
                     bg-white text-slate-900 dark:bg-slate-800 dark:text-slate-100
                     border-slate-300 dark:border-slate-600
                     focus:outline-none focus:ring-2 focus:ring-violet-400
                     placeholder-slate-400 dark:placeholder-slate-400 caret-violet-500"
                />
                <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                  Optionnel, laissez vide si vous ne savez pas ou pour un
                  débutant.
                </p>
              </div>
            </div>

            {/* Ceinture actuelle */}
            <div className="grid grid-cols-1 md:grid-cols-[180px_1fr] items-center gap-3">
              <label
                htmlFor="sel-belt"
                className="text-sm text-slate-800 dark:text-slate-200 md:text-right"
              >
                Ceinture actuelle
              </label>
              <div>
                <select
                  id="sel-belt"
                  name="belt_id"
                  value={formData.belt_id}
                  onChange={(e) =>
                    setFormData({ ...formData, belt_id: e.target.value })
                  }
                  className="input w-full px-3 py-2 rounded border
                     bg-white text-slate-900 dark:bg-slate-800 dark:text-slate-100
                     border-slate-300 dark:border-slate-600
                     focus:outline-none focus:ring-2 focus:ring-violet-400
                     placeholder-slate-400 dark:placeholder-slate-400 caret-violet-500"
                >
                  {belts.map((belt) => (
                    <option key={belt.id} value={belt.id}>
                      {belt.nom}
                    </option>
                  ))}
                </select>
                <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                  Pour un débutant, sélectionnez{" "}
                  <span className="italic">Ceinture Blanche</span>.
                </p>
              </div>
            </div>

            {/* Date de passage */}
            <div className="grid grid-cols-1 md:grid-cols-[180px_1fr] items-center gap-3">
              <label
                htmlFor="inp-date-passage"
                className="text-sm text-slate-800 dark:text-slate-200 md:text-right"
              >
                Date de passage
              </label>
              <input
                id="inp-date-passage"
                name="date_passage"
                type="date"
                value={formData.date_passage}
                onChange={handleChange}
                className="input w-full px-3 py-2 rounded border
                   bg-white text-slate-900 dark:bg-slate-800 dark:text-slate-100
                   border-slate-300 dark:border-slate-600
                   focus:outline-none focus:ring-2 focus:ring-violet-400
                   placeholder-slate-400 dark:placeholder-slate-400 caret-violet-500"
              />
            </div>

            {/* Numéro Pass'Sport */}
            <div className="grid grid-cols-1 md:grid-cols-[180px_1fr] items-center gap-3">
              <label
                htmlFor="inp-passsport"
                className="text-sm text-slate-800 dark:text-slate-200 md:text-right"
              >
                N° Pass’Sport (si éligible)
              </label>
              <input
                id="inp-passsport"
                name="passsport_number"
                type="text"
                value={formData.passsport_number}
                onChange={handleChange}
                placeholder="Saisir le numéro si applicable"
                className="input w-full px-3 py-2 rounded border
                   bg-white text-slate-900 dark:bg-slate-800 dark:text-slate-100
                   border-slate-300 dark:border-slate-600
                   focus:outline-none focus:ring-2 focus:ring-violet-400
                   placeholder-slate-400 dark:placeholder-slate-400 caret-violet-500"
              />
            </div>

            {/* Notes */}
            <div className="grid grid-cols-1 md:grid-cols-[180px_1fr] items-start gap-3">
              <label
                htmlFor="txt-notes"
                className="pt-2 text-sm text-slate-800 dark:text-slate-200 md:text-right"
              >
                Notes
              </label>
              <textarea
                id="txt-notes"
                name="notes_adherent"
                rows={4}
                value={formData.notes_adherent}
                onChange={(e) =>
                  setFormData({ ...formData, notes_adherent: e.target.value })
                }
                placeholder="Remarques, allergies, précisions utiles…"
                className="input w-full px-3 py-2 rounded border
                   bg-white text-slate-900 dark:bg-slate-800 dark:text-slate-100
                   border-slate-300 dark:border-slate-600
                   focus:outline-none focus:ring-2 focus:ring-violet-400
                   placeholder-slate-400 dark:placeholder-slate-400 caret-violet-500"
              />
            </div>
          </div>
        </section>

        {/* ÉTAPE 5 — Droit à l’image & règlement intérieur */}
        <section className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-4 md:p-5">
          <h3 className="text-base font-semibold text-slate-900 dark:text-slate-100">
            Étape 5 — Droit à l’image & règlement intérieur
          </h3>

          <div className="space-y-3 mt-3">
            {/* Droit à l'image */}
            <div className="grid grid-cols-1 md:grid-cols-[180px_1fr] items-start gap-3">
              <label
                htmlFor="chk-photo"
                className="text-sm text-slate-800 dark:text-slate-200 md:text-right"
              >
                Droit à l’image
              </label>
              <div className="flex items-start gap-3">
                <input
                  id="chk-photo"
                  type="checkbox"
                  checked={formData.autorisation_photo}
                  onChange={() =>
                    setFormData({
                      ...formData,
                      autorisation_photo: !formData.autorisation_photo,
                    })
                  }
                  className="mt-1 h-4 w-4 rounded border-slate-300 dark:border-slate-600 text-primary focus:ring-violet-400"
                />
                <div className="text-sm text-slate-700 dark:text-slate-200">
                  J’autorise l’association à diffuser les représentations
                  photographiques sur lesquelles pourrait apparaître l’adhérent,
                  prises lors des manifestations du club, sur supports
                  numériques et site internet, pour la durée la plus longue
                  prévue par la loi.
                  {!formData.autorisation_photo && (
                    <p className="mt-1 text-xs text-rose-700 dark:text-rose-300">
                      Vous ne souhaitez pas de diffusion d’images (droit à
                      l’image refusé).
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Règlement intérieur */}
            <div className="grid grid-cols-1 md:grid-cols-[180px_1fr] items-start gap-3">
              <label
                htmlFor="chk-ri"
                className="text-sm text-slate-800 dark:text-slate-200 md:text-right"
              >
                Règlement intérieur
              </label>
              <div className="flex items-start gap-3">
                <input
                  id="chk-ri"
                  type="checkbox"
                  required
                  className="mt-1 h-4 w-4 rounded border-slate-300 dark:border-slate-600 text-primary focus:ring-violet-400"
                />
                <div className="text-sm text-slate-700 dark:text-slate-200">
                  J’ai lu et j’accepte le{" "}
                  <a
                    href="/reglement-interieur"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary underline-offset-2 hover:underline"
                  >
                    règlement intérieur
                  </a>{" "}
                  du club.<span className="text-red-500 ml-1">*</span>
                  <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                    Cette case est obligatoire pour valider l’inscription.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <Button
          text="Valider l'inscription"
          className="mx-auto mb-4"
          disabled={!!groupError} // Désactivez le bouton si groupError est défini
        />
      </form>
      <ToastContainer />
    </div>
  );
};

export default InscriptionPage;
